import React, { useState, useEffect, Fragment } from "react";
import { XMarkIcon, ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { createTask, updateTask, getTask, getTaskStatuses, getTaskCategories, deleteTaskComment, getTaskFiles, uploadTaskFile, deleteTaskFile } from "../../Requests/TaskRequests";
import { getTeamMembers } from "../../Requests/TeamRequests";
import { getTeamUuid } from "../../utils/cookies";
import TaskFileUpload from "./TaskFileUpload";

export default function TaskForm({ task, statusId, project, statuses: propStatuses, categories: propCategories, onSave, onCancel, onDelete, open = true }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState(propStatuses || []);
  const [categories, setCategories] = useState(propCategories || []);
  const [subtasks, setSubtasks] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [fullTaskData, setFullTaskData] = useState(null);
  const [taskFiles, setTaskFiles] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    start_date: '',
    event_datetime: '',
    estimated_hours: '',
    team_id: getTeamUuid(),
    status_id: statusId,
    category_id: '',
    assignee_id: '',
    project_id: project?.id || '',
    project_uuid: project?.uuid || ''
  });

  useEffect(() => {
    // Load team members and any missing data
    const loadData = async () => {
      try {
        // Use team UUID from project prop if available, fallback to cookies
        let teamUuid = project?.team_uuid;
        
        // If no team_uuid, check if team_id looks like a UUID (contains dashes)
        if (!teamUuid && project?.team_id && typeof project.team_id === 'string' && project.team_id.includes('-')) {
          teamUuid = project.team_id;
        }
        
        // Fallback to current team from cookies if no UUID found
        if (!teamUuid) {
          teamUuid = getTeamUuid();
        }
        
        if (!teamUuid) {
          console.warn('No team UUID available, skipping team data loading');
          return;
        }
        
        // Only load statuses if not provided as props
        if (!propStatuses) {
          const statusesResponse = await getTaskStatuses(teamUuid);
          setStatuses(statusesResponse.data || []);
        } else {
          setStatuses(propStatuses);
        }
        
        // Only load categories if not provided as props
        if (!propCategories) {
          const categoriesResponse = await getTaskCategories(teamUuid);
          setCategories(categoriesResponse.data || []);
        } else {
          setCategories(propCategories);
        }
        
        // Always load team members (not cached)
        const membersResponse = await getTeamMembers(teamUuid);
        if (membersResponse.data) {
          // Handle different response structures - backend returns 'members' not 'users'
          const users = membersResponse.data.members || membersResponse.data.users || membersResponse.data.data?.users || membersResponse.data || [];
          setTeamUsers(Array.isArray(users) ? users : []);
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        // Handle authentication errors gracefully
        if (error.response?.status === 403) {
          console.warn('Access denied while loading task form data. User may lack permissions.');
          // Don't throw error, just continue with empty data
          setTeamUsers([]);
        }
      }
    };
    
    loadData();

    // Use fullTaskData if available (has complete relationships), otherwise use task prop
    const taskToUse = fullTaskData || task;
    
    // Use the most complete task data available
    
    // Reset state first to ensure clean initialization
    setSubtasks([]);
    setComments([]);
    
    if (taskToUse) {
      setFormData({
        title: taskToUse.title || '',
        description: taskToUse.description || '',
        priority: taskToUse.priority || 'medium',
        due_date: taskToUse.due_date ? 
          new Date(taskToUse.due_date).toISOString().slice(0, 16) : '',
        start_date: taskToUse.start_date ? 
          new Date(taskToUse.start_date).toISOString().slice(0, 16) : '',
        event_datetime: taskToUse.event_datetime ? 
          new Date(taskToUse.event_datetime).toISOString().slice(0, 16) : '',
        video_call_url: taskToUse.video_call_url || '',
        duration_minutes: taskToUse.duration_minutes || '',
        estimated_hours: taskToUse.estimated_hours || '',
        team_id: taskToUse.team_id || getTeamUuid(),
        status_id: taskToUse.status_id || statusId,
        category_id: taskToUse.category_id || '',
        assignee_id: taskToUse.assignee_id || '',
        project_id: taskToUse.project_id || project?.id || '',
        project_uuid: taskToUse.project_uuid || project?.uuid || ''
      });
      
      // Load subtasks if editing existing task
      if (taskToUse.subtasks && Array.isArray(taskToUse.subtasks)) {
        setSubtasks(taskToUse.subtasks);
      }
      
      // Load comments if editing existing task
      if (taskToUse.comments && Array.isArray(taskToUse.comments)) {
        const formattedComments = taskToUse.comments.map(comment => ({
          id: comment.id,
          text: comment.content,
          author: comment.user ? comment.user.name || comment.user.email : 'Unknown User',
          timestamp: new Date(comment.created_at).toLocaleString(),
          isNew: false
        }));
        setComments(formattedComments);
      }
    } else {
      // Reset form data for new task
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        start_date: '',
        event_datetime: '',
        video_call_url: '',
        duration_minutes: '',
        estimated_hours: '',
        team_id: getTeamUuid(),
        status_id: statusId,
        category_id: '',
        assignee_id: '',
        project_id: project?.id || '',
        project_uuid: project?.uuid || ''
      });
    }
  }, [task, fullTaskData, statusId, project, propStatuses, propCategories]);

  // Handle click outside for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
      }
      if (!event.target.closest('.status-dropdown')) {
        setShowStatusDropdown(false);
      }
      if (!event.target.closest('.assignee-dropdown')) {
        setShowAssigneeDropdown(false);
      }
      if (!event.target.closest('.priority-dropdown')) {
        setShowPriorityDropdown(false);
      }
    };

    if (showCategoryDropdown || showStatusDropdown || showAssigneeDropdown || showPriorityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown, showStatusDropdown, showAssigneeDropdown, showPriorityDropdown]);

  // Fetch full task data when task ID is provided (to get comments and other relationships)
  useEffect(() => {
    const fetchFullTaskData = async () => {
      if (task && task.id) {
        try {
          const response = await getTask(task.id);
          setFullTaskData(response.data);
        } catch (error) {
          console.error('Error fetching full task data:', error);
          // Fallback to the provided task data if API call fails
          setFullTaskData(task);
        }
      } else {
        setFullTaskData(null);
      }
    };

    fetchFullTaskData();
  }, [task?.id]);

  // Load task files when task ID is available
  useEffect(() => {
    const fetchTaskFiles = async () => {
      if (task && task.id) {
        try {
          const response = await getTaskFiles(task.id);
          setTaskFiles(response.data || []);
        } catch (error) {
          console.error('Error fetching task files:', error);
          setTaskFiles([]);
        }
      } else {
        setTaskFiles([]);
      }
    };

    fetchTaskFiles();
  }, [task?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate title is required and not just whitespace
    if (!formData.title || !formData.title.trim()) {
      alert(t('Tasks.TaskTitleRequired'));
      return;
    }
    
    setLoading(true);

    try {
      let result;
      const submitData = {
        ...formData,
        team_id: getTeamUuid(),
        project_id: project?.id,
        project_uuid: project?.uuid,
        subtasks: subtasks.filter(subtask => subtask.title && subtask.title.trim()),
        comments: comments.filter(comment => comment.text && comment.text.trim() && comment.isNew === true)
      };
      
      
      if (task) {
        result = await updateTask(task.id, submitData);
      } else {
        result = await createTask(submitData);
      }
      
      onSave(result.data);
    } catch (error) {
      console.error('Error saving task:', error);
      console.error('Error details:', error.response?.data);
      // Could show error toast here
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // Date validation and auto-update logic
      if (name === 'start_date' && value) {
        // If start date is set and due date exists, check if due date is before start date
        if (prev.due_date && new Date(value) > new Date(prev.due_date)) {
          // Auto-update due date to be same as start date if due date is before start date
          newData.due_date = value;
        }
      } else if (name === 'due_date' && value) {
        // If due date is being set, ensure it's not before start date
        if (prev.start_date && new Date(value) < new Date(prev.start_date)) {
          // Don't allow due date to be before start date - keep the old due date
          newData.due_date = prev.due_date;
          
          // Show user feedback
          setTimeout(() => {
            alert(t('Tasks.DueDateValidationError'));
          }, 100);
        }
      }

      return newData;
    });
  };

  const addSubtask = () => {
    const newSubtask = {
      id: Date.now(), // Temporary ID for new subtasks
      title: '',
      completed: false,
      isNew: true
    };
    setSubtasks([...subtasks, newSubtask]);
  };

  const updateSubtask = (id, field, value) => {
    setSubtasks(subtasks.map(subtask => 
      subtask.id === id ? { ...subtask, [field]: value } : subtask
    ));
  };

  const removeSubtask = (id) => {
    setSubtasks(subtasks.filter(subtask => subtask.id !== id));
  };

  const addComment = () => {
    if (newComment.trim()) {
      const comment = {
        id: Date.now(),
        text: newComment.trim(),
        author: 'Current User', // TODO: Get from auth context
        timestamp: new Date().toLocaleString(),
        isNew: true
      };
      setComments([...comments, comment]);
      setNewComment('');
    }
  };

  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addComment();
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteTaskComment(commentId);
      setComments(prevComments => 
        prevComments.filter(comment => comment.id !== commentId)
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert(t('Tasks.DeleteCommentError'));
    }
  };

  const handleDelete = () => {
    if (task) {
      setShowDeleteConfirmation(true);
    }
  };

  // File handling functions
  const handleFileUploaded = async (fileData) => {
    if (!task || !task.id) {
      throw new Error('Task must be saved before adding files');
    }

    const response = await uploadTaskFile(task.id, fileData);
    setTaskFiles(prev => [...prev, response.data]);
    return response.data;
  };

  const handleFileDeleted = async (fileId) => {
    if (!task || !task.id) return;

    try {
      await deleteTaskFile(task.id, fileId);
      setTaskFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(t('Tasks.DeleteFileError'));
    }
  };


  const handleConfirmDelete = () => {
    if (onDelete && task) {
      onDelete(task);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      case 'urgent': return 'text-red-800 bg-red-100 border-red-300';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return t('Tasks.HighPriority');
      case 'medium': return t('Tasks.MediumPriority'); 
      case 'low': return t('Tasks.LowPriority');
      case 'urgent': return t('Tasks.UrgentPriority');
      default: return t('Tasks.MediumPriority');
    }
  };

  const getSelectedCategory = () => {
    // Convert both to strings for comparison to handle type mismatches
    return categories.find(cat => String(cat.id) === String(formData.category_id));
  };

  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_id: categoryId
    }));
    setShowCategoryDropdown(false);
  };

  const handleStatusSelect = (statusId) => {
    setFormData(prev => ({
      ...prev,
      status_id: statusId
    }));
    setShowStatusDropdown(false);
  };

  const handleAssigneeSelect = (assigneeId) => {
    setFormData(prev => ({
      ...prev,
      assignee_id: assigneeId
    }));
    setShowAssigneeDropdown(false);
  };

  const handlePrioritySelect = (priority) => {
    setFormData(prev => ({
      ...prev,
      priority: priority
    }));
    setShowPriorityDropdown(false);
  };

  const getSelectedStatus = () => {
    return statuses.find(status => String(status.id) === String(formData.status_id));
  };

  const getSelectedAssignee = () => {
    return teamUsers.find(user => String(user.id) === String(formData.assignee_id));
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-2xl h-full">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
                      <button
                        type="button"
                        className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={onCancel}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  
                  <div className="flex h-screen flex-col bg-white shadow-xl overflow-hidden">
                    {showDeleteConfirmation ? (
                      /* Delete Confirmation View */
                      <>
                        {/* Header */}
                        <div className="bg-red-600 px-6 py-4">
                          <div className="flex items-center gap-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                            <h2 className="text-xl font-medium text-white">
                              {t('Tasks.DeleteTask')}
                            </h2>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 px-6 py-8">
                          <div className="space-y-6">
                            <div className="text-center">
                              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                              </div>
                              <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900">
                                  {t('Tasks.ConfirmDeleteTaskTitle')}
                                </h3>
                                <div className="mt-2">
                                  <p className="text-sm text-gray-500">
                                    {t('Tasks.ConfirmDeleteTaskMessage')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Task Info */}
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <h4 className="font-medium text-gray-900 mb-2">{t('Tasks.TaskToBeDeleted')}</h4>
                              <p className="text-gray-700 font-medium">{task?.title}</p>
                              {task?.description && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                          <button
                            type="button"
                            onClick={handleCancelDelete}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                          >
                            {t('Common.Cancel')}
                          </button>
                          <button
                            type="button"
                            onClick={handleConfirmDelete}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                          >
                            {t('Tasks.DeleteTask')}
                          </button>
                        </div>
                      </>
                    ) : (
                      /* Normal Task Form View */
                      <>
                    {/* Header with Title */}
                    <div className="bg-iavisus-100 px-6 py-4">
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        className="w-full text-xl font-medium text-white border-none outline-none bg-transparent placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-0"
                        placeholder={t('Tasks.TaskTitlePlaceholder')}
                      />
                    </div>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">

                      {/* Scrollable form fields */}
                      <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
                        <div className="space-y-6">
                          {/* Description */}
                          <div className="space-y-3">
                            <h3 className="text-sm font-medium text-gray-900">{t('Tasks.Description')}</h3>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              rows={3}
                              className="w-full p-3 border border-gray-200 rounded-lg outline-none resize-none bg-transparent placeholder-gray-400 text-gray-700 focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100"
                              placeholder={t('Tasks.AddDescriptionPlaceholder')}
                            />
                          </div>

                          {/* File Attachments */}
                          <div className="space-y-3">
                            <TaskFileUpload
                              taskId={task?.id}
                              files={taskFiles}
                              onFileUploaded={handleFileUploaded}
                              onFileDeleted={handleFileDeleted}
                              disabled={!task?.id}
                            />
                            {!task?.id && (
                              <p className="text-xs text-gray-500 italic">
                                {t('Tasks.SaveTaskFirstMessage')}
                              </p>
                            )}
                          </div>

                          {/* Task Details Grid */}
                          <div className="space-y-6">
                            {/* Status */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.Status')}</label>
                              <div className="relative min-w-[150px] status-dropdown">
                                <button
                                  type="button"
                                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                  className="bg-gray-50 text-black px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 outline-none focus:ring-2 focus:ring-iavisus-100 cursor-pointer min-w-[150px] flex items-center justify-between w-full"
                                >
                                  <span>{getSelectedStatus()?.name || t('Tasks.SelectStatus')}</span>
                                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showStatusDropdown && (
                                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                    {statuses.map(status => (
                                      <button
                                        key={status.id}
                                        type="button"
                                        onClick={() => handleStatusSelect(status.id)}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2"
                                      >
                                        <span>{status.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Assignee */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.Assignee')}</label>
                              <div className="relative min-w-[150px] assignee-dropdown">
                                <button
                                  type="button"
                                  onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                                  className="bg-gray-50 text-black px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 outline-none focus:ring-2 focus:ring-iavisus-100 cursor-pointer min-w-[150px] flex items-center justify-between w-full"
                                >
                                  <span>{getSelectedAssignee()?.name || getSelectedAssignee()?.email || t('Tasks.Unassigned')}</span>
                                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAssigneeDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showAssigneeDropdown && (
                                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                    <button
                                      type="button"
                                      onClick={() => handleAssigneeSelect('')}
                                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2"
                                    >
                                      <span>{t('Tasks.Unassigned')}</span>
                                    </button>
                                    {teamUsers.map(user => (
                                      <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => handleAssigneeSelect(user.id)}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2"
                                      >
                                        <span>{user.name || user.email}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Start Date */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.StartDate')}</label>
                              <input
                                type="datetime-local"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="text-gray-900 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100"
                              />
                            </div>

                            {/* Due Date */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.DueDate')}</label>
                              <input
                                type="datetime-local"
                                name="due_date"
                                value={formData.due_date}
                                onChange={handleChange}
                                min={formData.start_date || undefined}
                                className="text-gray-900 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100"
                              />
                            </div>

                            {/* Duration */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.Duration')}</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  name="duration_minutes"
                                  value={formData.duration_minutes}
                                  onChange={handleChange}
                                  min="1"
                                  max="1440"
                                  className="text-gray-900 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100 w-20"
                                  placeholder="60"
                                />
                                <span className="text-sm text-gray-500">{t('Tasks.Minutes')}</span>
                              </div>
                            </div>

                            {/* Video Call URL */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.VideoCall')}</label>
                              <input
                                type="url"
                                name="video_call_url"
                                value={formData.video_call_url}
                                onChange={handleChange}
                                className="flex-1 text-gray-900 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100"
                                placeholder={t('Tasks.VideoCallPlaceholder')}
                              />
                            </div>

                            {/* Priority */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.Priority')}</label>
                              <div className="relative min-w-[150px] priority-dropdown">
                                <button
                                  type="button"
                                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium border ${getPriorityColor(formData.priority)} outline-none focus:ring-2 focus:ring-iavisus-100 cursor-pointer min-w-[150px] flex items-center justify-between w-full`}
                                >
                                  <span>{getPriorityLabel(formData.priority)}</span>
                                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${showPriorityDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showPriorityDropdown && (
                                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                    {['low', 'medium', 'high', 'urgent'].map(priority => (
                                      <button
                                        key={priority}
                                        type="button"
                                        onClick={() => handlePrioritySelect(priority)}
                                        className={`w-full px-3 py-2 text-left text-sm rounded-lg hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2 m-1 ${getPriorityColor(priority)}`}
                                      >
                                        <span>{getPriorityLabel(priority)}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Category */}
                            <div className="flex items-center gap-4">
                              <label className="text-sm text-gray-500 w-20">{t('Tasks.Category')}</label>
                              <div className="relative min-w-[150px] category-dropdown">
                                <button
                                  type="button"
                                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                  className="bg-gray-50 text-black px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 outline-none focus:ring-2 focus:ring-iavisus-100 cursor-pointer min-w-[150px] flex items-center justify-between w-full"
                                >
                                  <div className="flex items-center gap-2">
                                    {getSelectedCategory() ? (
                                      <>
                                        <div 
                                          className="w-3 h-3 rounded-full" 
                                          style={{ backgroundColor: getSelectedCategory().color }}
                                        />
                                        <span>{getSelectedCategory().name}</span>
                                      </>
                                    ) : (
                                      <span>{t('Tasks.NoCategory')}</span>
                                    )}
                                  </div>
                                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showCategoryDropdown && (
                                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                    <button
                                      type="button"
                                      onClick={() => handleCategorySelect('')}
                                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2"
                                    >
                                      <div className="w-3 h-3" />
                                      <span>{t('Tasks.NoCategory')}</span>
                                    </button>
                                    {categories.map(category => (
                                      <button
                                        key={category.id}
                                        type="button"
                                        onClick={() => handleCategorySelect(category.id)}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-iavisus-100 hover:bg-opacity-20 flex items-center gap-2"
                                      >
                                        <div 
                                          className="w-3 h-3 rounded-full" 
                                          style={{ backgroundColor: category.color }}
                                        />
                                        <span>{category.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>


                          {/* Sub-tasks */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h3 className="text-sm font-medium text-gray-900">{t('Tasks.SubTasks')}</h3>
                              <button
                                type="button"
                                onClick={addSubtask}
                                className="text-sm text-white bg-iavisus-100 hover:bg-iavisus-200 px-3 py-1 rounded-lg transition-colors"
                              >
                                {t('Tasks.AddSubtask')}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {subtasks.map((subtask) => (
                                <div key={subtask.id} className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={subtask.completed}
                                    onChange={(e) => updateSubtask(subtask.id, 'completed', e.target.checked)}
                                    className="w-4 h-4 text-iavisus-100 rounded focus:ring-iavisus-100"
                                  />
                                  <input
                                    type="text"
                                    value={subtask.title}
                                    onChange={(e) => updateSubtask(subtask.id, 'title', e.target.value)}
                                    className="flex-1 text-sm bg-transparent border-none outline-none focus:ring-0"
                                    placeholder={t('Tasks.SubtaskPlaceholder')}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSubtask(subtask.id)}
                                    className="text-gray-400 hover:text-red-500"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              {subtasks.length === 0 && (
                                <p className="text-sm text-gray-400">{t('Tasks.NoSubTasksYet')}</p>
                              )}
                            </div>
                          </div>

                          {/* Comments */}
                          <div className="space-y-3">
                            <h3 className="text-sm font-medium text-gray-900">{t('Tasks.Comments')}</h3>
                            
                            {/* Existing Comments */}
                            {comments.length > 0 ? (
                              <div className="space-y-3">
                                {comments.map((comment) => (
                                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm font-medium text-gray-900">{comment.author}</span>
                                          <span className="text-xs text-gray-500">{comment.timestamp}</span>
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                                      </div>
                                      {/* Delete button for comments (only show for non-new comments that have database IDs) */}
                                      {!comment.isNew && comment.id && (
                                        <button
                                          onClick={() => handleDeleteComment(comment.id)}
                                          className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                          title={t('Tasks.DeleteComment')}
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">{t('Tasks.NoCommentsYet')}</p>
                            )}
                            
                            {/* Add New Comment */}
                            <div className="space-y-2">
                              <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onKeyPress={handleCommentKeyPress}
                                rows={2}
                                className="w-full p-3 border border-gray-200 rounded-lg outline-none resize-none bg-transparent placeholder-gray-400 text-gray-700 focus:ring-2 focus:ring-iavisus-100 focus:border-iavisus-100"
                                placeholder={t('Tasks.AddCommentPlaceholder')}
                              />
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={addComment}
                                  disabled={!newComment.trim()}
                                  className="px-3 py-1 bg-iavisus-100 text-white rounded-lg hover:bg-iavisus-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {t('Tasks.AddComment')}
                                </button>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>

                      {/* Form Actions - Always Visible */}
                      <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                        {/* Delete Button */}
                        {task && onDelete && (
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="px-4 py-2 text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors font-medium"
                          >
                            {t('Tasks.Delete')}
                          </button>
                        )}
                        
                        {/* Cancel/Save Buttons */}
                        <div className="flex gap-3 ml-auto">
                          <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                          >
                            {t('Common.Cancel')}
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-iavisus-100 text-white rounded-lg hover:bg-iavisus-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? t('Common.Saving') : (task ? t('Common.Update') : t('Tasks.CreateTask'))}
                          </button>
                        </div>
                      </div>
                    </form>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}